import type { EsmsTopRecipient } from '../../types/esms';
import InfoTooltip from '../revenue/InfoTooltip';
import EsmsErrorBox from './EsmsErrorBox';

function fmt(v: number): string { return v.toLocaleString('en-US'); }

interface Props {
  recipients: EsmsTopRecipient[];
  isDark: boolean;
  isLoading: boolean;
  error: string | null;
  disabled: boolean;
}

export default function EsmsTopRecipientsTable({ recipients, isDark, isLoading, error, disabled }: Props) {
  return (
    <div className={`backdrop-blur rounded-lg p-4 sm:p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Top số nhận SMS
        <InfoTooltip isDark={isDark} text={'Top 20 số điện thoại nhận nhiều SMS nhất trong kỳ.\nChỉ hỗ trợ khoảng thời gian ≤ 31 ngày.\n\nNguồn: eSMS GetSmsSentData_V2.'} />
      </h3>

      {disabled ? (
        <div className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
          Tính năng này chỉ khả dụng khi khoảng thời gian ≤ 31 ngày. Hãy thu hẹp phạm vi để xem.
        </div>
      ) : isLoading ? (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đang tải...</div>
      ) : error ? (
        <EsmsErrorBox error={error} isDark={isDark} compact />
      ) : recipients.length === 0 ? (
        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chưa có dữ liệu</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}>
                <th className={`text-left px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Số điện thoại</th>
                <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Số SMS</th>
                <th className={`text-right px-2 sm:px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Tổng chi phí (VND)</th>
                <th className={`hidden sm:table-cell text-left px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Loại</th>
                <th className={`hidden sm:table-cell text-left px-3 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Lần cuối</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.phone} className={isDark ? 'border-b border-white/5' : 'border-b border-gray-100'}>
                  <td className={`px-2 sm:px-3 py-2 font-mono ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>{r.phone}</td>
                  <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-cyan-300' : 'text-gray-900'}`}>{fmt(r.count)}</td>
                  <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-amber-300' : 'text-gray-900'}`}>{fmt(r.totalCost)}</td>
                  <td className={`hidden sm:table-cell px-3 py-2 text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {r.types.map((t) => t.label).join(', ')}
                  </td>
                  <td className={`hidden sm:table-cell px-3 py-2 text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{r.lastSent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
